import React, { Component } from "react";
import { Link } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";
import "./Posts.css";

class Posts extends Component {
  constructor(props) {
    super(props);

    this.state = {
      posts: [],
      loading: true,
      searchTerm: "",
      currentPage: 1,
      pageSize: 10,
    };
  }

  componentDidMount() {
    this.fetchPosts();
  }

  fetchPosts = async () => {
    this.setState({ loading: true });

    try {
      const res = await axios.get("/posts");

      if (res.data.status === "success") {
        this.setState({
          posts: Array.isArray(res.data.data) ? res.data.data : [],
          loading: false,
        });
      } else {
        this.setState({ loading: false });
        Swal.fire(
          "Error",
          res.data.message || "Failed to load posts",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
      this.setState({ loading: false });
      Swal.fire("Error", "Failed to fetch posts", "error");
    }
  };

  handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (confirm.isConfirmed) {
      try {
        const res = await axios.delete(`/posts/${id}`);

        if (res.data.status === "success") {
          Swal.fire(
            "Deleted!",
            res.data.message || "Post deleted.",
            "success"
          );
          this.fetchPosts();
        } else {
          Swal.fire(
            "Error",
            res.data.message || "Failed to delete post",
            "error"
          );
        }
      } catch (err) {
        console.error("Error deleting post:", err);
        Swal.fire("Error", "Failed to delete post", "error");
      }
    }
  };

  handleSearchChange = (event) => {
    this.setState({ searchTerm: event.target.value, currentPage: 1 });
  };

  clearSearch = () => {
    this.setState({ searchTerm: "", currentPage: 1 });
  };

  filterPosts = (posts, searchTerm) => {
    const term = String(searchTerm || "").trim().toLowerCase();
    if (!term) return posts;

    return posts.filter((post) => {
      const searchableText = [
        post.id, post.title, post.category_name, post.tag_ids,
        post.facility_ids, post.regular_price, post.sales_price,
        post.post_state, post.status,
      ]
        .filter((value) => value !== null && value !== undefined && value !== "")
        .join(" ")
        .toLowerCase();

      return searchableText.includes(term);
    });
  };

  handlePageChange = (page) => {
    const { posts, pageSize, searchTerm } = this.state;
    const filteredPosts = this.filterPosts(posts, searchTerm);
    const totalPages = Math.max(1, Math.ceil(filteredPosts.length / pageSize));

    if (page < 1 || page > totalPages) return;

    this.setState({ currentPage: page });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  handlePageSizeChange = (event) => {
    const pageSize = Number(event.target.value);

    this.setState({
      pageSize,
      currentPage: 1,
    });
  };

  truncateText = (text, length = 34) => {
    if (!text) return "N/A";
    return text.length > length ? `${text.substring(0, length)}...` : text;
  };

  formatPrice = (value) => {
    if (value === null || value === undefined || value === "") return "N/A";

    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) return value;

    return numericValue.toLocaleString("en-BD");
  };

  getPageNumbers = () => {
    const { posts, pageSize, currentPage } = this.state;
    const totalPages = Math.max(1, Math.ceil(posts.length / pageSize));

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, "ellipsis", totalPages];
    }

    if (currentPage >= totalPages - 3) {
      return [
        1,
        "ellipsis",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [
      1,
      "ellipsis",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "ellipsis-end",
      totalPages,
    ];
  };

  renderStatus = (post) => {
    const active = post.post_state === "1" || post.status === "active";

    return (
      <span
        className={`posts-status-badge ${active ? "is-active" : "is-inactive"}`}
      >
        <span className="posts-status-dot" />
        {active ? "Active" : "Inactive"}
      </span>
    );
  };

  render() {
    const {
      posts,
      loading,
      searchTerm,
      currentPage,
      pageSize,
    } = this.state;

    const baseUrl = axios.defaults.baseURL
      ? axios.defaults.baseURL.replace("/api", "")
      : "";

    const filteredPosts = this.filterPosts(posts, searchTerm);
    const totalPosts = posts.length;
    const filteredTotal = filteredPosts.length;
    const totalPages = Math.max(1, Math.ceil(filteredTotal / pageSize));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = filteredTotal > 0 ? (safeCurrentPage - 1) * pageSize : 0;
    const endIndex = Math.min(startIndex + pageSize, filteredTotal);
    const currentPosts = filteredPosts.slice(startIndex, endIndex);
    const pageNumbers = this.getPageNumbers();

    return (
      <div className="pc-container posts-management">
        <div className="pc-content">
          <div className="row">
            <div className="col-12">
              <div className="posts-card">
                {/* Header */}
                <div className="posts-card-header">
                  <div className="posts-heading-area">
                    <div className="posts-title-icon">
                      <i className="ph ph-files" />
                    </div>
                    <div>
                      <h4 className="posts-title">Posts</h4>
                      <div className="posts-breadcrumb">
                        <span>Posts</span>
                        <i className="ph ph-caret-right" />
                        <span>List</span>
                      </div>
                    </div>
                  </div>

                  <Link to="/admin/post_form" className="posts-add-btn">
                    <i className="ph ph-plus" />
                    <span>New Post</span>
                  </Link>
                </div>

                {/* Toolbar */}
                <div className="posts-toolbar">
                  <div className="posts-toolbar-left">
                    <div className="posts-summary">
                      <span className="posts-summary-label">All Posts</span>
                      <span className="posts-summary-count">{totalPosts}</span>
                    </div>

                    {searchTerm && (
                      <div className="posts-search-result">
                        <i className="ph ph-funnel-simple" />
                        <span>{filteredTotal} result{filteredTotal !== 1 ? "s" : ""}</span>
                      </div>
                    )}
                  </div>

                  <div className="posts-toolbar-right">
                    <div className="posts-search">
                      <i className="ph ph-magnifying-glass" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={this.handleSearchChange}
                        placeholder="Search posts..."
                        aria-label="Search posts"
                      />
                      {searchTerm && (
                        <button
                          type="button"
                          className="posts-search-clear"
                          onClick={this.clearSearch}
                          aria-label="Clear search"
                          title="Clear search"
                        >
                          <i className="ph ph-x" />
                        </button>
                      )}
                    </div>

                    <div className="posts-page-size">
                      <label htmlFor="postsPageSize">Show</label>
                      <select
                        id="postsPageSize"
                        value={pageSize}
                        onChange={this.handlePageSizeChange}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                      </select>
                      <span>entries</span>
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="posts-table-shell">
                  <div className="posts-table-scroll">
                    <table className="posts-table">
                      <thead>
                        <tr>
                          <th className="posts-col-number">#</th>
                          <th className="posts-col-image">Image</th>
                          <th className="posts-col-title">Title</th>
                          <th className="posts-col-category">Category</th>
                          <th className="posts-col-tags">Tags</th>
                          <th className="posts-col-facilities">Facilities</th>
                          <th className="posts-col-price">Price (Reg / Sale)</th>
                          <th className="posts-col-status">Status</th>
                          <th className="posts-col-actions">Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={9} className="posts-empty-cell">
                              <div className="posts-loading-state">
                                <div className="posts-spinner" role="status">
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                                <span>Loading posts...</span>
                              </div>
                            </td>
                          </tr>
                        ) : currentPosts.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="posts-empty-cell">
                              <div className="posts-no-data">
                                <div className="posts-empty-icon">
                                  <i className="ph ph-file-x" />
                                </div>
                                <h5>No Posts Found</h5>
                                <p>
                                  {searchTerm
                                    ? `No posts matched "${searchTerm}". Try another search.`
                                    : 'Click “New Post” to create your first post.'}
                                </p>
                                {searchTerm ? (
                                  <button
                                    type="button"
                                    className="posts-empty-btn"
                                    onClick={this.clearSearch}
                                  >
                                    <i className="ph ph-x" /> Clear Search
                                  </button>
                                ) : (
                                  <Link
                                    to="/admin/post_form"
                                    className="posts-empty-btn"
                                  >
                                    <i className="ph ph-plus" /> New Post
                                  </Link>
                                )}
                              </div>
                            </td>
                          </tr>
                        ) : (
                          currentPosts.map((p, index) => (
                            <tr key={p.id}>
                              <td className="posts-number-cell">
                                {startIndex + index + 1}
                              </td>

                              <td>
                                <div className="posts-image-wrap">
                                  {p.feature_image || p.image_id ? (
                                    <img
                                      src={`${baseUrl}/uploads/posts/${
                                        p.feature_image || p.image_id
                                      }`}
                                      alt={p.title || "Post"}
                                      className="posts-image"
                                    />
                                  ) : (
                                    <div className="posts-image-placeholder">
                                      <i className="ph ph-image" />
                                    </div>
                                  )}
                                </div>
                              </td>

                              <td className="posts-title-cell" title={p.title || "N/A"}>
                                <a
                                  href={`/listing/${p.id}`}
                                  className="post-title-link"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {this.truncateText(p.title)}
                                </a>
                              </td>

                              <td>
                                <span className="posts-category-cell">
                                  {p.category_name || "N/A"}
                                </span>
                              </td>

                              <td>
                                <div
                                  className="posts-text-cell"
                                  title={p.tag_ids || "N/A"}
                                >
                                  {this.truncateText(p.tag_ids, 42)}
                                </div>
                              </td>

                              <td>
                                <div
                                  className="posts-text-cell"
                                  title={p.facility_ids || "N/A"}
                                >
                                  {this.truncateText(p.facility_ids, 42)}
                                </div>
                              </td>

                              <td>
                                <div className="posts-price-cell">
                                  <span className="posts-regular-price">
                                    {p.regular_price !== null &&
                                    p.regular_price !== undefined &&
                                    p.regular_price !== ""
                                      ? `৳ ${this.formatPrice(p.regular_price)}`
                                      : "N/A"}
                                  </span>
                                  <span className="posts-sale-price">
                                    {p.sales_price !== null &&
                                    p.sales_price !== undefined &&
                                    p.sales_price !== ""
                                      ? `৳ ${this.formatPrice(p.sales_price)}`
                                      : "N/A"}
                                  </span>
                                </div>
                              </td>

                              <td>{this.renderStatus(p)}</td>

                              <td>
                                <div className="posts-actions">
                                  <Link
                                    to={`/admin/post_form/${p.id}`}
                                    className="posts-action-btn posts-edit-btn"
                                    title="Edit Post"
                                  >
                                    <i className="ph ph-note-pencil" />
                                    <span>Edit</span>
                                  </Link>

                                  <button
                                    type="button"
                                    onClick={() => this.handleDelete(p.id)}
                                    className="posts-action-btn posts-delete-btn"
                                    title="Delete Post"
                                  >
                                    <i className="ph ph-trash" />
                                    <span>Delete</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                {!loading && filteredTotal > 0 && (
                  <div className="posts-pagination-bar">
                    <div className="posts-pagination-info">
                      Showing <strong>{startIndex + 1}</strong> to{" "}
                      <strong>{endIndex}</strong> of{" "}
                      <strong>{filteredTotal}</strong> {searchTerm ? "matching posts" : "posts"}
                    </div>

                    <nav
                      className="posts-pagination"
                      aria-label="Posts pagination"
                    >
                      <button
                        type="button"
                        className="posts-page-btn posts-page-nav"
                        disabled={safeCurrentPage === 1}
                        onClick={() =>
                          this.handlePageChange(safeCurrentPage - 1)
                        }
                        aria-label="Previous page"
                      >
                        <i className="ph ph-caret-left" />
                      </button>

                      {pageNumbers.map((page, index) =>
                        typeof page === "number" ? (
                          <button
                            type="button"
                            key={`${page}-${index}`}
                            className={`posts-page-btn ${
                              safeCurrentPage === page ? "active" : ""
                            }`}
                            onClick={() => this.handlePageChange(page)}
                            aria-current={
                              safeCurrentPage === page ? "page" : undefined
                            }
                          >
                            {page}
                          </button>
                        ) : (
                          <span
                            key={`${page}-${index}`}
                            className="posts-page-dots"
                            aria-hidden="true"
                          >
                            •••
                          </span>
                        )
                      )}

                      <button
                        type="button"
                        className="posts-page-btn posts-page-nav"
                        disabled={safeCurrentPage === totalPages}
                        onClick={() =>
                          this.handlePageChange(safeCurrentPage + 1)
                        }
                        aria-label="Next page"
                      >
                        <i className="ph ph-caret-right" />
                      </button>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Posts;
